/**
 * UserDetailPanel
 *
 * Slide-over panel for a selected user, showing:
 *   - Groups tab: memberships with add/remove controls
 *   - Resolved Access tab: effective merged policy
 *
 * Props:
 *   user           {Object}   - User object
 *   onClose        {function} - Close callback
 *   onGroupsChanged {function?} - Called after membership changes
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Search, Shield, UserMinus, X } from "lucide-react";
import useFeedback from "../../hooks/useFeedback";
import {
  addGroupMember,
  getUserGroups,
  getUserResolvedPolicy,
  listAdminGroups,
  removeGroupMember,
} from "../../api/adminGroups";
import { AuthError } from "../../api";
import Loader from "../Loader/Loader";
import UserAuthStore from "../../store/userStore";
import logger from "../../services/Logger";

// ─── Badges ───────────────────────────────────────────────────────────────────

const SourceBadge = ({ source }) => {
  const isScim = source === "scim";
  return (
    <span
      className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${
        isScim
          ? "bg-fill-hex-faf5ff dark:bg-fill-hex-581c87/20 text-ink-hex-7e22ce dark:text-ink-hex-c084fc border-edge-hex-e9d5ff dark:border-edge-hex-7e22ce"
          : "bg-fill-blue-50 dark:bg-fill-blue-900/20 text-ink-blue-700 dark:text-ink-blue-400 border-edge-blue-200 dark:border-edge-blue-700"
      }`}
    >
      {isScim ? "IdP SCIM" : "Photon"}
    </span>
  );
};

const AccessBadge = ({ access }) => {
  if (access === "allow") {
    return (
      <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-fill-green-50 dark:bg-fill-green-900/20 text-ink-green-700 dark:text-ink-green-400 border-edge-green-200 dark:border-edge-green-700">
        Allow
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-fill-red-50 dark:bg-fill-red-900/20 text-ink-red-700 dark:text-ink-red-400 border-edge-red-200 dark:border-edge-red-700">
      Deny
    </span>
  );
};

// ─── Tab bar ──────────────────────────────────────────────────────────────────

const TABS = ["Groups", "Resolved Access"];

const TabBar = ({ active, onChange }) => (
  <div className="flex border-b border-edge-border dark:border-edge-gray-700">
    {TABS.map((tab) => (
      <button
        key={tab}
        onClick={() => onChange(tab)}
        className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
          active === tab
            ? "text-ink-gradient-primary border-b-2 border-edge-gradient-primary -mb-px"
            : "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-ink dark:hover:text-ink-white"
        }`}
      >
        {tab}
      </button>
    ))}
  </div>
);

// ─── Groups tab ───────────────────────────────────────────────────────────────

const GroupsTab = ({ user, orgId, onGroupsChanged }) => {
  const { showFeedback } = useFeedback();
  const [groups, setGroups] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addQuery, setAddQuery] = useState("");
  const [addingId, setAddingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const addDropdownRef = useRef(null);
  const addButtonRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [userGroups, orgGroups] = await Promise.all([
        getUserGroups(user.user_id, orgId),
        listAdminGroups(orgId),
      ]);
      if (!mountedRef.current) return;
      setGroups(userGroups ?? []);
      setAllGroups(orgGroups ?? []);
    } catch (err) {
      if (!(err instanceof AuthError)) {
        logger.error("UserDetailPanel", "Error loading groups", { error: err.message });
        showFeedback("Failed to load groups.", "error");
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [user.user_id, orgId, showFeedback]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!addOpen) return;
    const handler = (e) => {
      if (
        addDropdownRef.current && !addDropdownRef.current.contains(e.target) &&
        addButtonRef.current && !addButtonRef.current.contains(e.target)
      ) {
        setAddOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [addOpen]);

  const memberGroupIds = new Set(groups.map((g) => g.group_id));
  const candidateGroups = allGroups.filter(
    (g) =>
      g.source === "photon_native" &&
      !memberGroupIds.has(g.group_id) &&
      (!addQuery ||
        (g.display_name || "").toLowerCase().includes(addQuery.toLowerCase()))
  );

  const handleAdd = async (groupId) => {
    setAddingId(groupId);
    try {
      await addGroupMember(groupId, user.user_id);
      setAddOpen(false);
      setAddQuery("");
      await fetchData();
      if (onGroupsChanged) onGroupsChanged();
      showFeedback("Added to group.", "success");
    } catch {
      showFeedback("Failed to add to group.", "error");
    } finally {
      if (mountedRef.current) setAddingId(null);
    }
  };

  const handleRemove = async (groupId) => {
    setRemovingId(groupId);
    try {
      await removeGroupMember(groupId, user.user_id);
      await fetchData();
      if (onGroupsChanged) onGroupsChanged();
      showFeedback("Removed from group.", "success");
    } catch {
      showFeedback("Failed to remove from group.", "error");
    } finally {
      if (mountedRef.current) setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Add to group */}
      <div className="relative">
        <button
          ref={addButtonRef}
          onClick={() => setAddOpen((v) => !v)}
          className="flex items-center gap-1.5 text-sm text-ink-gradient-primary hover:underline"
        >
          Add to group
          <ChevronDown className="w-3 h-3" />
        </button>

        <AnimatePresence>
          {addOpen && (
            <motion.div
              ref={addDropdownRef}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15 }}
              className="absolute top-8 left-0 z-50 w-64 bg-surface-dialog border border-edge-border dark:border-edge-gray-700 rounded-xl shadow-lg overflow-hidden"
            >
              <div className="p-2 border-b border-edge-border dark:border-edge-gray-700">
                <div className="flex items-center gap-2 bg-fill-navigation-hover dark:bg-fill-navigation-surface-dark rounded-lg px-2 py-1.5">
                  <Search className="w-3 h-3 text-ink-gray-400 flex-shrink-0" />
                  <input
                    autoFocus
                    value={addQuery}
                    onChange={(e) => setAddQuery(e.target.value)}
                    placeholder="Search groups…"
                    className="flex-1 text-xs bg-transparent outline-none text-ink-ink dark:text-ink-white placeholder-ink-gray-400"
                  />
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto">
                {candidateGroups.length === 0 ? (
                  <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 p-3">
                    {addQuery ? "No groups match." : "No available Photon-native groups."}
                  </p>
                ) : (
                  candidateGroups.map((g) => (
                    <button
                      key={g.group_id}
                      onClick={() => handleAdd(g.group_id)}
                      disabled={addingId === g.group_id}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-fill-navigation-hover dark:hover:bg-fill-navigation-surface-dark text-left transition-colors"
                    >
                      <div className="w-5 h-5 rounded-full bg-fill-add-surface dark:bg-fill-gradient-primary/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-[9px] font-bold text-ink-gradient-primary">G</span>
                      </div>
                      <span className="text-xs text-ink-ink dark:text-ink-white flex-1 truncate">
                        {g.display_name || g.group_id}
                      </span>
                      {addingId === g.group_id && <Loader className="w-3 h-3" />}
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Group memberships */}
      {groups.length === 0 ? (
        <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 py-4 text-center">
          Not a member of any groups.
        </p>
      ) : (
        <div className="flex flex-col divide-y divide-edge-border dark:divide-edge-gray-700">
          {groups.map((g) => (
            <div key={g.group_id} className="flex items-center gap-3 py-3">
              <div className="w-8 h-8 rounded-full bg-fill-add-surface dark:bg-fill-gradient-primary/20 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-ink-gradient-primary">G</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink-ink dark:text-ink-white truncate">
                  {g.display_name || g.group_id}
                </p>
                {g.description && (
                  <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 truncate">{g.description}</p>
                )}
              </div>
              <SourceBadge source={g.source} />
              {g.membership_source === "manual" && (
                <button
                  onClick={() => handleRemove(g.group_id)}
                  disabled={removingId === g.group_id}
                  className="p-1.5 rounded-lg text-ink-gray-400 hover:text-ink-red-500 hover:bg-fill-red-50 dark:hover:bg-fill-red-900/20 transition-colors disabled:opacity-50"
                  title="Remove from group"
                >
                  {removingId === g.group_id ? (
                    <Loader className="w-3 h-3" />
                  ) : (
                    <UserMinus className="w-4 h-4" />
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Resolved Access tab ──────────────────────────────────────────────────────

/** Build flat rows [{resource_id, type, access, reason}] from a resolved policy doc. */
function buildAccessRows(policy) {
  if (!policy) return [];
  const rows = [];
  const types = [
    { key: "models", label: "Model" },
    { key: "agents", label: "Agent" },
    { key: "skills", label: "Skill" },
  ];
  types.forEach(({ key, label }) => {
    const allowed = policy[`allowed_${key}`] ?? [];
    const denied = policy[`denied_${key}`] ?? [];
    denied.forEach((id) => {
      rows.push({
        id: id === "*" ? `All ${(label || "").toLowerCase()}s` : id,
        type: label,
        access: "deny",
        reason: id === "*" ? "Wildcard deny" : "Explicit deny",
      });
    });
    allowed.forEach((id) => {
      rows.push({
        id: id === "*" ? `All ${(label || "").toLowerCase()}s` : id,
        type: label,
        access: "allow",
        reason: id === "*" ? "Wildcard allow" : "Group allows",
      });
    });
  });
  return rows;
}

const ResolvedAccessTab = ({ user, orgId }) => {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getUserResolvedPolicy(user.user_id, orgId)
      .then((p) => { if (mountedRef.current) { setPolicy(p); setLoading(false); } })
      .catch((err) => {
        logger.error("UserDetailPanel", "Error loading resolved policy", { error: err.message });
        if (mountedRef.current) { setError("Failed to load resolved access."); setLoading(false); }
      });
  }, [user.user_id, orgId]);

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader />
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-ink-red-500 dark:text-ink-red-400 py-4">{error}</p>;
  }

  const rows = buildAccessRows(policy);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 text-xs text-ink-gray-500 dark:text-ink-gray-400 bg-fill-navigation-hover dark:bg-fill-card-surface-dark px-3 py-2 rounded-lg">
        <Shield className="w-3.5 h-3.5 flex-shrink-0" />
        Effective access after merging all group policies and org defaults.
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 py-4 text-center">
          No explicit access rules. Org default policy applies.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-edge-border dark:border-edge-gray-700">
                <th className="text-left py-2 pr-3 font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide">Resource</th>
                <th className="text-left py-2 pr-3 font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide">Type</th>
                <th className="text-left py-2 pr-3 font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide">Access</th>
                <th className="text-left py-2 font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-edge-hex-eeeeee dark:divide-edge-gray-700">
              {rows.map((row, i) => (
                <tr key={i}>
                  <td className="py-2 pr-3 text-ink-ink dark:text-ink-white font-mono truncate max-w-[140px]">{row.id}</td>
                  <td className="py-2 pr-3 text-ink-gray-500 dark:text-ink-gray-400">{row.type}</td>
                  <td className="py-2 pr-3"><AccessBadge access={row.access} /></td>
                  <td className="py-2 text-ink-gray-500 dark:text-ink-gray-400">{row.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Quota summary */}
      {policy && (
        <div className="mt-2">
          <p className="text-xs font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wide mb-2">Quotas</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: "quota_daily_tokens", label: "Daily tokens" },
              { key: "quota_monthly_tokens", label: "Monthly tokens" },
              { key: "quota_requests_per_minute", label: "RPM" },
              { key: "quota_daily_image_generations", label: "Daily images" },
              { key: "quota_daily_video_generations", label: "Daily videos" },
              { key: "quota_daily_audio_generations", label: "Daily audio" },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between text-xs py-1.5 px-2 bg-fill-gray-50 dark:bg-fill-card-surface-dark rounded">
                <span className="text-ink-gray-500 dark:text-ink-gray-400">{label}</span>
                <span className="font-medium text-ink-ink dark:text-ink-white">
                  {policy[key] != null ? policy[key].toLocaleString() : "No limit"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── UserDetailPanel ──────────────────────────────────────────────────────────

const UserDetailPanel = ({ user, onClose, onGroupsChanged }) => {
  const [activeTab, setActiveTab] = useState("Groups");
  const orgId = UserAuthStore.getState().orgId;

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-fill-black/30"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />

      {/* Panel */}
      <motion.div
        className="relative w-full max-w-[520px] h-full bg-surface-detail shadow-2xl flex flex-col overflow-hidden"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "tween", duration: 0.25 }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-edge-border dark:border-edge-gray-700 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-ink-ink dark:text-ink-white truncate">
              {user.name || user.email || user.user_id}
            </h2>
            {user.name && user.email && (
              <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5 truncate">{user.email}</p>
            )}
            <p className="text-[10px] text-ink-gray-400 dark:text-ink-gray-500 mt-0.5 font-mono">{user.user_id}</p>
          </div>
          <button
            onClick={onClose}
            className="ml-3 p-1.5 rounded-lg text-ink-gray-400 hover:text-ink-gray-600 dark:hover:text-ink-white hover:bg-fill-gray-100 dark:hover:bg-fill-gray-700 transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <TabBar active={activeTab} onChange={setActiveTab} />

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {activeTab === "Groups" && (
            <GroupsTab user={user} orgId={orgId} onGroupsChanged={onGroupsChanged} />
          )}
          {activeTab === "Resolved Access" && (
            <ResolvedAccessTab user={user} orgId={orgId} />
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default UserDetailPanel;
